<script lang="ts">
	import { goto } from '$app/navigation';
	import { marketsConfig } from '$lib/config/markets';
	import { userMarginList } from '$lib/home/userInteract/store';
	import { getDisplayName, getTokenLogo } from '$lib/perps';
	import type { Market, MarketIdentifiers } from '$lib/types/market';
	import { getNumBackInUnits, nFormatter, toBN } from '$lib/util/util';
	import { walletConnected } from '$lib/wallet/store';
	import BigNumber from 'bignumber.js';

	export let market: Market;

	function openMarket() {
		goto(`/perps?market=${market.marketIdentifier}`);
	}
	function getUserMargin(marketId: MarketIdentifiers) {
		if ($userMarginList) {
			// from getUserMargin return the depositedMargin for the markerId
			let depositedMargin = $userMarginList.find((margin) => margin?.marketIdentifier == marketId)
				?.depositedMargin as string;
			if (depositedMargin) return getNumBackInUnits(depositedMargin, 18, 2);
		}
		return '';
	}

	$: skewRate =
		market && market.longOI && market.shortOI
			? new BigNumber(market.longOI)
					.div(new BigNumber(market.shortOI).plus(market.longOI))
					.toNumber()
			: 0.5;

	$: isChangePositive = new BigNumber(toBN(market.change24h).toString()).gte(0);
</script>

<!-- Desktop View -->
<div
	class="hidden sm:block mt-5 w-full cursor-pointer"
	on:click={openMarket}
	on:keydown={openMarket}
	role="presentation"
>
	<div class="hidden sm:grid sm:grid-cols-6 items-start w-full">
		<div class="flex gap-2">
			<img
				loading="lazy"
				src={getTokenLogo(market.marketIdentifier)}
				alt="logo"
				class="w-5 h-5 rounded-full"
			/>
			<div class="text-fx-grey-100 text-sm rounded-lg squircle-lg font-medium">
				{getDisplayName(market.marketIdentifier)}
			</div>
		</div>
		<div class="text-fx-grey-100 text-sm font-medium text-center">
			${getNumBackInUnits(market.currentMarketPrice, 18, marketsConfig[market.asset].showDecimals)}
		</div>
		<div class="flex flex-col gap-px items-start justify-center mx-auto">
			<div
				class="text-fx-gradient-22 text-sm font-medium text-center"
				class:text-fx-gradient-23={isChangePositive}
			>
				{market.change24hPercentage}%
			</div>
			<div class="text-fx-grey-300 text-xs font-medium text-center">
				{isChangePositive ? '' : '-'}${getNumBackInUnits(
					toBN(market.change24h).abs().toString(),
					18,
					2
				)}
			</div>
		</div>

		<div class="text-fx-grey-100 text-sm font-medium text-center">
			{nFormatter(getNumBackInUnits(market.volume24h ?? 0, 18, 2), 2)}
		</div>
		<div
			class="text-fx-gradient-22 text-sm font-medium text-center"
			class:text-fx-gradient-23={new BigNumber(market.fundingRatePercentageLast1h).gte(0)}
		>
			{getNumBackInUnits(market.fundingRatePercentageLast1h, 18, 4)}%
		</div>
		<div class="flex flex-col gap-px items-start justify-center mx-auto">
			<div class="text-fx-grey-100 text-sm font-medium text-center">
				${nFormatter(getNumBackInUnits(market.openInterest, 18, 2), 2)}
			</div>
		</div>
	</div>
</div>

<!-- Mobile view -->
<div
	class="sm:hidden block w-full mb-2"
	on:click={openMarket}
	on:keydown={openMarket}
	role="presentation"
>
	<div>
		<div
			class="flex gap-3 px-2.5 py-2 rounded-lg squircle-lg bg-fx-gradient-8 cursor-pointer text-fx-grey-300 hover:text-fx-grey-200 hover:bg-fx-grey-700 transform duration-200 ease-out items-center w-full"
		>
			<img
				loading="lazy"
				src={getTokenLogo(market.marketIdentifier)}
				alt="logo"
				class="w-8 h-8 rounded-full"
			/>
			<div class="grid">
				<div class="text-sm rounded-lg squircle-lg font-medium">
					{getDisplayName(market.marketIdentifier)}
				</div>
				<div class="flex gap-1">
					<div class="text-fx-white font-medium text-xs">
						${getNumBackInUnits(
							market.currentMarketPrice,
							18,
							marketsConfig[market.asset].showDecimals
						)}
					</div>
					<div
						class=" text-xs font-medium"
						class:text-fx-gradient-23={isChangePositive}
						class:text-fx-gradient-22={!isChangePositive}
					>
						{getNumBackInUnits(
							new BigNumber(toBN(market.change24h).toString())
								.div(new BigNumber(market.currentMarketPrice))
								.multipliedBy(1e20)
								.toString(),
							18,
							2
						)}%
					</div>
				</div>
			</div>
			<div class="grow" />
			{#if $walletConnected}
				<div class="text-right">
					<div class="text-fx-grey-200 font-medium text-xs flex gap-1">
						<span class="text-fx-grey-400"> Margin </span>
						{#if getUserMargin(market.marketIdentifier)}
							{getUserMargin(market.marketIdentifier)}
						{:else}
							<div
								class="animate-pulse bg-fx-grey-800 h-4 col-span-2 rounded-lg squircle-lg w-6 mx-auto"
							/>
						{/if}
					</div>
				</div>
			{:else}
				<div class="text-right">
					<div class="text-fx-grey-200 font-medium text-xs flex gap-1">
						<span class="text-fx-grey-400"> 24h Volume: </span>
						${nFormatter(getNumBackInUnits(market.volume24h ?? 0, 18, 2), 0)}
					</div>
				</div>
			{/if}
		</div>
	</div>
</div>
